import { localeHref, localeTo } from "@/lib/locale";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Sparkles, ArrowRight, HeartHandshake, Stethoscope, Lock, Mail, KeyRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

import { PasswordInput } from "@/components/ui/password-input";
import { SubmitButton } from "@/components/ui/submit-button";
import { FieldError } from "@/components/ui/field-error";
import { friendlyAuthError } from "@/lib/friendly-errors";
import { markSessionEphemeral } from "@/lib/remember-me";
import { EmailActionModal } from "@/components/auth/EmailActionModal";
import { track } from "@/lib/track";
import { isPreLaunchNow } from "@/lib/launch-mode";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/login")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): { redirect?: string; next?: string; resend?: string } => {
    const out: { redirect?: string; next?: string; resend?: string } = {};
    if (typeof search.redirect === "string") out.redirect = search.redirect;
    if (typeof search.next === "string") out.next = search.next;
    if (typeof search.resend === "string") out.resend = search.resend;
    return out;
  },
  head: () => ({
    meta: [
      { title: "Log in — Recovery Trauma™" },
      {
        name: "description",
        content: "Sign in to your Recovery Trauma™ member account.",
      },
      { property: "og:title", content: "Log in — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Sign in to your Recovery Trauma™ member account.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/login" }],
  }),
  component: LoginRoute,
});

function LoginRoute() {
  // Account creation & login are open pre-launch so members can verify email
  // and access billing / their pre-launch welcome. Member tools remain gated
  // behind LaunchLockGate until 1 August 2026.
  return <MemberLoginPage />;
}


function MemberLoginPage() {
  const navigate = useNavigate();
  const { redirect: redirectTo, next: nextTo, resend: resendParam } = Route.useSearch();
  // `next` (from the email-confirmation success screen) takes precedence
  // over `redirect` (from route-guard bounces). Preserving `next` through
  // sign-in sends verified-but-unpaid users to Step 2 of 4 on /membership.
  const postLoginTarget = nextTo ?? redirectTo;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(resendParam === "1");
  const [quickLoading, setQuickLoading] = useState<"apple" | "google" | null>(null);

  const isSafeTarget = (target?: string) =>
    !!target && target.startsWith("/") && !target.startsWith("//");

  // Resolve the post-login destination BEFORE navigating so paid Founding
  // Members go straight to /youre-in — never via /dashboard, which would
  // briefly render the member shell before MemberGate bounced them and
  // produce a visible flash of "Recovery Trauma™ Dashboard".
  const resolvePostLoginTarget = async (userId: string): Promise<string> => {
    if (isSafeTarget(postLoginTarget)) return postLoginTarget!;
    try {
      // Admin short-circuit: real dashboard is theirs.
      const { data: isAdmin } = await supabase.rpc("has_role", {
        _user_id: userId,
        _role: "admin",
      } as never);
      if (isAdmin) return "/dashboard";
      const { data: membership } = await supabase
        .from("memberships")
        .select("status, paid_at, stripe_subscription_id, complimentary_grant")
        .eq("user_id", userId)
        .maybeSingle();
      const isPaidMember =
        membership?.status === "active" &&
        (!!membership.paid_at ||
          !!membership.stripe_subscription_id ||
          !!membership.complimentary_grant);
      // Pre-launch: paid Members wait on /youre-in. Once the platform is
      // live (1 Aug 2026) they must go straight to their dashboard — the
      // holding page would otherwise show a finished countdown forever.
      if (isPaidMember) return isPreLaunchNow() ? "/youre-in" : "/dashboard";
    } catch { /* fall through to onboarding funnel */ }
    // Unpaid confirmed users go to the dedicated payment page — never to
    // /dashboard (would flash DashboardHome) or /membership (guest funnel).
    return "/complete-membership";
  };

  useEffect(() => {
    let cancelled = false;
    const go = async (userId: string) => {
      try { sessionStorage.removeItem("rt_pending_verify_email"); } catch { /* noop */ }
      const target = await resolvePostLoginTarget(userId);
      if (cancelled) return;
      window.location.assign(localeTo(target));
    };
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data.user) return;
      void go(data.user.id);
    });
    const onAuthChange = (event: Event) => {
      const detail = (event as CustomEvent<{ event?: string; session?: { user?: { id?: string } } }>).detail;
      const userId = detail?.session?.user?.id;
      if (detail?.event === "SIGNED_IN" && userId) void go(userId);
    };
    window.addEventListener("rt-auth-change", onAuthChange);
    return () => {
      cancelled = true;
      window.removeEventListener("rt-auth-change", onAuthChange);
    };
  }, [postLoginTarget]);


  const handleOAuth = async (provider: "apple" | "google") => {
    if (quickLoading || loading) return;
    setError(""); setInfo(""); setQuickLoading(provider);
    try {
      const target = isSafeTarget(postLoginTarget) ? postLoginTarget : "/dashboard";
      sessionStorage.setItem("oauth_pending", provider);
      sessionStorage.setItem("oauth_redirect", target ?? "/dashboard");
      markSessionEphemeral(remember);
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
        extraParams: provider === "google" ? { prompt: "select_account" } : undefined,
      });
      if (result.error) throw result.error;
      if (!result.redirected) {
        const { data } = await supabase.auth.getUser();
        if (data.user) {
          const destination = await resolvePostLoginTarget(data.user.id);
          window.location.assign(localeTo(destination));
        }
      }
    } catch {
      sessionStorage.removeItem("oauth_pending");
      sessionStorage.removeItem("oauth_redirect");
      setError(`We couldn't sign you in with ${provider === "apple" ? "Apple" : "Google"}. Please try again or use your password.`);
    } finally { setQuickLoading(null); }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(""); setInfo("");

    // Validate BEFORE anything else. Previously an empty field produced a
    // "missing email" round-trip that looked like a failed login attempt.
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      const message = !trimmedEmail
        ? "Please enter your email address."
        : "Please enter your password.";
      setError(message);
      track("login_validation_error", { reason: !trimmedEmail ? "missing_email" : "missing_password" }, { source: "login_page" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmedEmail)) {
      setError("That doesn't look like a valid email address.");
      track("login_validation_error", { reason: "invalid_email" }, { source: "login_page" });
      return;
    }

    setLoading(true);
    track("login_submit", {}, { source: "login_page" });

    // Bound the sign-in call. On mobile Safari / in-app browsers the auth
    // client can stall indefinitely (Web Locks), which is why so many
    // submits produced no success and no error at all — the user just saw
    // a spinner forever and left.
    const TIMEOUT_MS = 15_000;
    let timedOut = false;
    const result = await Promise.race([
      supabase.auth
        .signInWithPassword({ email: trimmedEmail, password })
        .catch((err: unknown) => ({
          data: null,
          error: { message: err instanceof Error ? err.message : "network_error" },
        })),
      new Promise<null>((resolve) =>
        setTimeout(() => {
          timedOut = true;
          resolve(null);
        }, TIMEOUT_MS),
      ),
    ]);
    setLoading(false);

    if (timedOut || !result) {
      setError(
        "Signing in is taking longer than expected. Please check your connection and try again — if you're inside the Instagram or Facebook browser, opening this page in Safari or Chrome usually fixes it.",
      );
      track("login_timeout", { timeout_ms: TIMEOUT_MS }, { source: "login_page" });
      return;
    }

    const { data, error: signInError } = result as {
      data: { session?: unknown } | null;
      error: { message?: string } | null;
    };

    if (signInError) {
      setError(friendlyAuthError(signInError as never));
      track("login_error", { message: (signInError.message ?? "").slice(0, 120) }, { source: "login_page" });
      // Fire-and-forget: record for the admin health monitor. Errors here
      // must never block sign-in UX or leak to the console.
      try {
        void supabase.rpc("record_auth_failure" as never, {
          _email: trimmedEmail,
          _reason: signInError.message ?? "unknown",
        } as never);
      } catch { /* noop */ }
      return;
    }
    track("login_success", {}, { source: "login_page" });
    if (data?.session) markSessionEphemeral(remember);
  };

  const handleForgotSubmit = async (target: string) => {
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: localeHref("/reset-password"),
    });
    // Neutral response even on failure to avoid account enumeration; log
    // only truly unexpected errors and always return the same message.
    if (resetError && !/rate|not.?found/i.test(resetError.message ?? "")) {
      // Swallow other errors silently — the message stays neutral.
    }
    return "If an account exists for this email address, we've sent you a password reset email. Please check your inbox, spam, and junk folders.";
  };

  const handleResendSubmit = async (target: string) => {
    // If we can see locally that the current viewer is already verified,
    // give the definitive already-confirmed message instead of the neutral one.
    const { readVerifiedStatus } = await import("@/lib/verify-status");
    const status = readVerifiedStatus();
    if (
      status.verified &&
      status.email &&
      status.email.toLowerCase() === target.toLowerCase()
    ) {
      return "This email address has already been confirmed. You can sign in to your Recovery Trauma™ account using your email and password.";
    }
    // Ask the server whether an account exists BEFORE enqueueing a
    // confirmation email. Previously we called supabase.auth.resend for any
    // address, which produced "sent" log rows and real emails whose tokens
    // bound to no account — recipients kept getting "we couldn't confirm
    // your link" forever. Now we only resend when there is a genuine
    // pending-verification user, and direct everyone else to the right flow.
    try {
      const { lookupAccountStatusForResend } = await import("@/lib/onboarding.functions");
      const res = await lookupAccountStatusForResend({ data: { email: target } });
      if (res.status === "no_account") {
        return "We couldn't find a Recovery Trauma™ account for that email address. Please create an account to get started — you'll receive your confirmation email right away.";
      }
      if (res.status === "already_confirmed") {
        return "This email address has already been confirmed. You can sign in to your Recovery Trauma™ account using your email and password.";
      }
      // status === "pending_confirmation" — safe to send a fresh link.
      await supabase.auth.resend({ type: "signup", email: target });
      return "We've sent a fresh confirmation email. Please check your inbox, spam, and junk folders — the newest link is the one that will work.";
    } catch {
      // Fallback: neutral response so a transient lookup failure doesn't
      // reveal state or block the user entirely.
      return "If this account still needs confirmation, we've sent a new confirmation email. If you've already clicked a confirmation link, no new email will be sent — sign in with your email and password to continue.";
    }
  };

  return (
    <div className="signup-page flex min-h-[80vh] w-full min-w-0 max-w-full items-center justify-center overflow-x-hidden px-4 py-12">
      <div className="w-full min-w-0 max-w-[calc(100vw-2rem)] sm:max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
            <HeartHandshake className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-serif text-2xl text-foreground">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your Recovery Trauma™ account.</p>
        </div>

        <div className="mb-5 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" className="min-h-12" disabled={!!quickLoading || loading} onClick={() => void handleOAuth("apple")}><span aria-hidden className="text-base">●</span>{quickLoading === "apple" ? "Opening…" : "Apple"}</Button>
            <Button type="button" variant="outline" className="min-h-12" disabled={!!quickLoading || loading} onClick={() => void handleOAuth("google")}><span aria-hidden className="font-semibold">G</span>{quickLoading === "google" ? "Opening…" : "Google"}</Button>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3 text-[11px] text-muted-foreground"><span className="h-px flex-1 bg-border"/><span>or use your password</span><span className="h-px flex-1 bg-border"/></div>
        <p className="mb-4 rounded-lg border border-gold/20 bg-gold/5 px-3 py-2 text-[11px] leading-relaxed text-foreground/70">
          Enter the email and password you used when you created your Recovery
          Trauma™ account. If you can't sign in, see <strong>Need help
          signing in?</strong> below.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3" noValidate>


          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-foreground/80"><Mail className="h-3.5 w-3.5" /> Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              className="w-full min-h-[48px] rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-gold"
            />
          </label>
          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-foreground/80"><KeyRound className="h-3.5 w-3.5" /> Password</span>
            <PasswordInput
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              enterKeyHint="go"
              showLockIcon={false}
              className="w-full min-h-[48px] rounded-lg border border-input bg-background px-3 pe-12 text-sm outline-none focus:border-gold"
            />
          </label>

          {/* Prominent, always-visible password recovery — the small link in
              the help section below was being missed (98% of sign-in
              failures are wrong email/password). */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                track("cta_click", { cta: "forgot_password_open", placement: "inline" }, { source: "login_page" });
                setForgotOpen(true);
              }}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-[var(--gold-deep)] underline-offset-2 transition hover:underline"
            >
              <KeyRound className="h-3.5 w-3.5" />
              Forgot password?
            </button>
          </div>



          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-input text-[var(--gold-deep)] focus:ring-ring"
            />
            Remember me on this device
          </label>

          <FieldError message={error} />
          {/* Contextual next step, right where the failure is read. */}
          {/verify your email|not confirmed/i.test(error) && (
            <button
              type="button"
              onClick={() => {
                track("cta_click", { cta: "resend_confirmation_open", placement: "error" }, { source: "login_page" });
                setResendOpen(true);
              }}
              className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border border-gold/50 bg-gold/10 px-3 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold/20"
            >
              <Mail className="h-4 w-4" />
              Send me a new confirmation email
            </button>
          )}
          {/incorrect email or password/i.test(error) && (
            <button
              type="button"
              onClick={() => {
                track("cta_click", { cta: "forgot_password_open", placement: "error" }, { source: "login_page" });
                setForgotOpen(true);
              }}
              className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border border-gold/50 bg-gold/10 px-3 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold/20"
            >
              <KeyRound className="h-4 w-4" />
              Reset my password
            </button>
          )}
          {info && <p role="status" className="rounded-md bg-gold/10 px-3 py-2 text-sm text-[var(--gold-deep)]">{info}</p>}

          <SubmitButton
            pending={loading}
            pendingLabel="Signing in…"
            trailingIcon={<ArrowRight className="h-4 w-4" />}
            className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-[var(--gradient-gold)] px-4 text-sm font-display font-medium text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Sign in
          </SubmitButton>
        </form>

        <div className="mt-4 rounded-xl border border-gold/30 bg-gold/5 p-3 text-center">
          <p className="text-xs text-muted-foreground">New to Recovery Trauma™?</p>
          <Link
            to="/signup"
            onClick={() => track("cta_click", { cta: "create_account" }, { source: "login_page" })}
            className="mt-2 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-gold/50 bg-background px-4 text-sm font-semibold text-[var(--gold-deep)] shadow-sm transition hover:bg-gold/10"
          >
            <HeartHandshake className="h-4 w-4" aria-hidden />
            Create Account
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        <section className="mt-6 rounded-lg border border-gold/30 bg-cream/40 p-4">
          <h2 className="font-display text-sm text-foreground">
            Need help signing in?
          </h2>

          <div className="mt-3">
            <p className="text-xs font-semibold text-foreground">
              Forgot your password?
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              We'll email you a secure link to reset your password.
            </p>
            <button
              type="button"
              onClick={() => {
                track("cta_click", { cta: "forgot_password_open" }, { source: "login_page" });
                setForgotOpen(true);
              }}
              className="mt-2 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md border border-gold/40 bg-background px-3 text-xs font-medium text-[var(--gold-deep)] transition hover:bg-gold/10"
            >
              <KeyRound className="h-3.5 w-3.5" />
              Forgot password
            </button>
          </div>

          <div className="mt-5 border-t border-gold/20 pt-4">
            <p className="text-xs font-semibold text-foreground">
              Didn't receive your confirmation email?
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              If you've already clicked a confirmation link, sign in instead —
              confirmed accounts do not receive another confirmation email.
            </p>
            <button
              type="button"
              onClick={() => {
                track("cta_click", { cta: "resend_confirmation_open" }, { source: "login_page" });
                setResendOpen(true);
              }}
              className="mt-3 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md border border-gold/40 bg-background px-3 text-xs font-medium text-[var(--gold-deep)] transition hover:bg-gold/10"
            >
              <Mail className="h-3.5 w-3.5" />
              Resend confirmation email
            </button>
          </div>
        </section>

        <EmailActionModal
          open={forgotOpen}
          onOpenChange={setForgotOpen}
          title="Forgot your password?"
          description="Enter the email address associated with your Recovery Trauma™ account and we'll send you a password reset link."
          submitLabel="Send Reset Link"
          initialEmail={email}
          onSubmit={handleForgotSubmit}
        />

        <EmailActionModal
          open={resendOpen}
          onOpenChange={setResendOpen}
          title="Resend confirmation email"
          description="Use this only if you have not clicked a confirmation link yet. If your email is already confirmed, no new email will be sent — sign in with your email and password instead."
          submitLabel="Resend Confirmation Email"
          initialEmail={email}
          onSubmit={handleResendSubmit}
        />


        <p className="mt-8 text-center text-xs text-muted-foreground">Healing Beyond Survival Mode™</p>
      </div>
    </div>
  );
}

function LockedLoginPage() {
  return (
    <div className="min-h-dvh bg-background px-4 py-16 sm:py-24">
      <div className="mx-auto w-full max-w-xl rounded-3xl border border-gold/30 bg-card p-8 text-center shadow-sm sm:p-12">
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" /> Members welcome
        </span>
        <h1 className="mt-5 font-display text-3xl text-foreground sm:text-4xl">Log in to Recovery Trauma™</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Recovery Trauma<span className="text-[var(--gold-deep)]">™</span> is open. Sign in to your account, or
          create one to start your Membership.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <Link to="/" hash="founding-cta"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-3 font-display text-sm font-medium text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5">
            <HeartHandshake className="h-4 w-4" /> Create Account <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/therapist-application"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-6 py-3 font-display text-sm font-medium text-[var(--gold-deep)] backdrop-blur transition hover:-translate-y-0.5 hover:bg-cream">
            <Stethoscope className="h-4 w-4" /> Apply as a Therapist
          </Link>
        </div>
        <div className="mt-8 border-t border-gold/20 pt-5">
          <Link to="/admin-login" className="inline-flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground transition hover:text-[var(--gold-deep)]">
            <Lock className="h-3.5 w-3.5" /> Admin sign in
          </Link>
        </div>
        <p className="mt-6 text-xs text-muted-foreground">Healing Beyond Survival Mode™</p>
      </div>
    </div>
  );
}
